import { useCallback, useEffect, useRef, useState } from 'react';
import telegramService from '../services/telegram';
import {
  classifyGeoFailure, pickGeoSource, GEO_POLL_MS, GEO_LOST_RETRY_MS,
  GEO_GRACE_MS, GEO_GIVEUP_MS, GEO_RESPONSE_TIMEOUT_MS, GEO_STALE_MS,
} from '../services/geolocation';

// Ответ на просьбу о геолокации — общий для всех маршрутов: спрашивать
// заново на каждом это назойливость, а не забота
export const GEO_CONSENT_KEY = 'route_geo_consent';

// Плашка диагностики геолокации. Включается пятью быстрыми тапами
// по кнопке геолокации — проверить слежение на телефоне без консоли
export const GEO_DEBUG_KEY = 'geo_debug';
const DEBUG_TAPS = 5;
const DEBUG_TAPS_WINDOW_MS = 3000;

/**
 * LocationManager — только внутри настоящего Telegram.
 *
 * Вне его @twa-dev/sdk подставляет заглушку: объект есть, а getLocation
 * никогда не вызывает колбэк. Код уходил в ветку Telegram, браузерный
 * watchPosition не запускался, и позиция не появлялась никогда —
 * в вебе расстояние до точки не показывалось вовсе.
 */
const getLocationManager = () =>
  (telegramService.isRunningInTelegram && window.Telegram?.WebApp?.LocationManager) || null;

const getPlatform = () => window.Telegram?.WebApp?.platform || 'unknown';

const readDebugFlag = () => {
  try { return localStorage.getItem(GEO_DEBUG_KEY) === '1'; } catch { return false; }
};

/**
 * Геолокация на прогулке — общая для маршрута редакции и своего маршрута.
 *
 * Правила (CLAUDE.md, «Геолокация»):
 * - свой вопрос «Поделитесь геолокацией» — до системного, ответ
 *   в `route_geo_consent`, отказ не переспрашиваем;
 * - в Telegram `getLocation` без выданного доступа сам поднимает
 *   системное окно, поэтому повторяем его, только если координаты
 *   уже реально приходили, и конечное число раз (`GEO_LOST_RETRY_MS`);
 * - на Android Telegram отдаёт из getLocation устаревшую точку —
 *   там слежение ведёт браузерный watchPosition (`pickGeoSource`);
 * - причины сбоя разбирает `classifyGeoFailure` (на неё есть тесты).
 *
 * Возвращает позицию, слежение карты и всё, что нужно окнам `GeoModals`.
 */
const useWalkGeolocation = () => {
  const [userPosition, setUserPosition] = useState(null);
  const [userAccuracy, setUserAccuracy] = useState(null); // метры
  const [geoBlocked, setGeoBlocked] = useState(false); // для обновления UI кнопки
  // Слежение карты. Выключается, когда человек сам двигает карту,
  // возвращается кнопкой геолокации
  const [followMap, setFollowMap] = useState(true);
  const [showGeoAccessModal, setShowGeoAccessModal] = useState(false);
  // Почему нет позиции: 'denied' — доступ запрещён, 'unavailable' —
  // геолокация выключена на телефоне, 'unsupported' — устройство не умеет.
  // Инструкции у этих случаев разные, одной общей не обойтись
  const [geoErrorKind, setGeoErrorKind] = useState('denied');
  const [geoRetrying, setGeoRetrying] = useState(false);
  const [showGeoPromptModal, setShowGeoPromptModal] = useState(false);
  const [geoDebug, setGeoDebug] = useState(readDebugFlag);

  const watchIdRef = useRef(null);
  const geoIntervalRef = useRef(null);  // таймер следующего опроса Telegram LocationManager
  const geoBlockedRef = useRef(false);  // true = опрос остановлен, не спамим
  // Запрос к Telegram ещё в полёте — не шлём поверх него следующий
  const geoPendingRef = useRef(false);
  // Предел ожидания ответа на текущий запрос
  const geoResponseTimerRef = useRef(null);
  // Номер текущего запроса. SDK копит колбэки и на очередной ответ зовёт
  // все разом — устаревшие (после таймаута или остановки) отбрасываем
  const geoRequestIdRef = useRef(0);
  // Откуда берём координаты: 'telegram' | 'browser' | 'none'
  const sourceRef = useRef(null);
  // Когда пришла последняя позиция. 0 = ни разу
  const lastFixAtRef = useRef(0);
  // Когда начался текущий период без позиции. 0 = позиция в порядке
  const geoFailSinceRef = useRef(0);
  // Панель открыта нами по временному сбою — закроем её сами, когда отпустит
  const geoTransientPanelRef = useRef(false);
  // Доступ к гео подтверждён: координаты приходили или клиент прямо
  // говорит, что доступ выдан. Без этого повторять getLocation нельзя —
  // каждый вызов поднимает системный запрос Telegram
  const geoGrantedRef = useRef(false);
  // Сколько тихих попыток вернуть слежение уже сделано после потери сигнала
  const geoLostRetriesRef = useRef(0);
  // Счётчики для плашки диагностики
  const statsRef = useRef({ fixes: 0, misses: 0, timeouts: 0, restarts: 0, lastError: null });
  const debugTapsRef = useRef([]);

  /**
   * Показывает панель с инструкцией под конкретную причину. Браузер отдаёт
   * код ошибки: 1 — доступ запрещён, 2 — позицию не определить (обычно
   * выключена геолокация на телефоне). Telegram кода не даёт: там
   * getLocation просто возвращает null, и причину приходится оставлять
   * на выбор пользователя — оба сценария описаны в панели
   */
  const showGeoError = useCallback((err) => {
    const lm = sourceRef.current === 'telegram' ? getLocationManager() : null;
    const { kind } = classifyGeoFailure(err, lm, geoGrantedRef.current);
    geoTransientPanelRef.current = false;
    setGeoErrorKind(kind);
    setGeoRetrying(false);
    setShowGeoAccessModal(true);
  }, []);

  /** Гасим и браузерный watch, и опрос Telegram — что из них работало */
  const stopWatching = useCallback(() => {
    if (watchIdRef.current && watchIdRef.current !== 'telegram') {
      navigator.geolocation?.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = null;
    clearTimeout(geoIntervalRef.current);
    geoIntervalRef.current = null;
    clearTimeout(geoResponseTimerRef.current);
    geoResponseTimerRef.current = null;
    // Ответ на запрос, отправленный до остановки, уже никому не нужен
    geoRequestIdRef.current += 1;
    geoPendingRef.current = false;
  }, []);

  /**
   * Позиция пришла. Обнуляем период сбоев и убираем панель, если это мы
   * подняли её по временной потере сигнала: причина ушла сама, читать
   * инструкцию больше незачем. Панель, открытую по запрету или по кнопке,
   * не трогаем — там человек должен увидеть ответ сам
   */
  const acceptPosition = useCallback((lat, lng, accuracy) => {
    geoFailSinceRef.current = 0;
    geoGrantedRef.current = true;
    geoLostRetriesRef.current = 0;
    lastFixAtRef.current = Date.now();
    statsRef.current.fixes += 1;
    setUserPosition([lat, lng]);
    // Точность — для круга точности. Не приходит — не рисуем
    setUserAccuracy(Number.isFinite(accuracy) ? accuracy : null);
    if (geoTransientPanelRef.current) {
      geoTransientPanelRef.current = false;
      setShowGeoAccessModal(false);
    }
  }, []);

  /**
   * Сбой в фоновом слежении — в отличие от showGeoError, здесь человек
   * ничего не нажимал и не ждёт ответа.
   *
   * Запрет и отсутствие поддержки показываем сразу и опрос гасим: сами
   * они не пройдут. Потерю сигнала терпим молча — она проходит, — и
   * говорим о ней, только если держится дольше GEO_GRACE_MS.
   */
  const reportGeoFailure = useCallback((err) => {
    statsRef.current.misses += 1;
    if (err) statsRef.current.lastError = `code ${err.code}: ${err.message || ''}`;

    // В Telegram любой промах окончателен, и автоповторов нет вовсе.
    // getLocation на клиенте, где доступ не выдан или геолокация выключена
    // в системе, сам поднимает системное окно — опрос по кругу превращался
    // в очередь окон каждые 15–20 секунд. Отличить «отказ» от «нет сигнала»
    // по флагам не выходит: клиент отвечает isAccessGranted, а координат
    // всё равно не даёт. Поэтому решаем просто: промах — встаём.
    // Вернуть слежение можно кнопкой геолокации на карте.
    if (sourceRef.current === 'telegram') {
      const lm = getLocationManager();
      // Координаты уже приходили — значит доступ точно в порядке и это
      // потеря сигнала: арка, метро, двор-колодец. На таком повторе окна
      // не будет, поэтому несколько раз тихо пробуем вернуться.
      // Признак берём только по реально пришедшим координатам: флаг
      // isAccessGranted врёт ровно в плохом случае — отвечает «выдан»,
      // когда геолокация выключена в системе
      if (geoGrantedRef.current
          && geoLostRetriesRef.current < GEO_LOST_RETRY_MS.length) {
        return GEO_LOST_RETRY_MS[geoLostRetriesRef.current++];
      }

      stopWatching();
      geoBlockedRef.current = true;
      setGeoBlocked(true);

      // Попытки исчерпаны, но доступ был — значит сигнала нет всерьёз.
      // Молчим: метка остаётся на последнем месте, а панель посреди
      // прогулки только мешает. Вернуть слежение можно кнопкой
      if (geoGrantedRef.current) return 0;

      const { kind } = classifyGeoFailure(err, lm, false);
      if (kind === 'denied') {
        try { localStorage.setItem(GEO_CONSENT_KEY, 'denied'); } catch {}
      }
      geoTransientPanelRef.current = false;
      setGeoErrorKind(kind);
      setGeoRetrying(false);
      setShowGeoAccessModal(true);
      return 0;
    }

    // Браузер: watchPosition системных окон не поднимает и повторяет сам —
    // паузу возвращать нечего, сбои терпим и говорим о них, только
    // если затянулось
    const { terminal, kind } = classifyGeoFailure(err, null, geoGrantedRef.current);

    if (terminal) {
      stopWatching();
      geoBlockedRef.current = true;
      setGeoBlocked(true);
      // Запоминаем отказ: при следующем входе на прогулку не спрашиваем
      // заново. Передумать можно кнопкой геолокации на карте
      if (kind === 'denied') {
        try { localStorage.setItem(GEO_CONSENT_KEY, 'denied'); } catch {}
      }
      geoTransientPanelRef.current = false;
      setGeoErrorKind(kind);
      setGeoRetrying(false);
      setShowGeoAccessModal(true);
      return 0;
    }

    if (!geoFailSinceRef.current) geoFailSinceRef.current = Date.now();
    const failingFor = Date.now() - geoFailSinceRef.current;

    // Панель поднимаем один раз за серию сбоев. Без этой проверки она
    // переоткрывалась на каждом промахе: человек закрыл — через пять
    // секунд она снова перед ним, и так до бесконечности
    if (failingFor >= GEO_GRACE_MS && !geoTransientPanelRef.current) {
      geoTransientPanelRef.current = true;
      setGeoErrorKind(kind);
      setGeoRetrying(false);
      setShowGeoAccessModal(true);
    }
    // Попытки не бесконечны: страховка для клиентов без флагов доступа
    if (failingFor >= GEO_GIVEUP_MS) {
      stopWatching();
      geoBlockedRef.current = true;
      setGeoBlocked(true);
    }
    return 0;
  }, [stopWatching]);

  // Запуск отслеживания после согласия пользователя
  const startWatchingLocation = useCallback(() => {
    if (watchIdRef.current) return;
    const lm = getLocationManager();
    const source = pickGeoSource(lm, getPlatform(), !!navigator.geolocation);
    sourceRef.current = source;

    const doStart = () => {
      if (source === 'telegram') {
        // Telegram: опрашиваем каждые 5 секунд, пока координаты приходят.
        // Первый же промах останавливает цикл — см. reportGeoFailure
        geoBlockedRef.current = false;
        geoFailSinceRef.current = 0;
        watchIdRef.current = 'telegram';

        const schedule = (ms) => {
          clearTimeout(geoIntervalRef.current);
          geoIntervalRef.current = setTimeout(poll, ms);
        };

        // Промах: null от Telegram, исключение или нет ответа вовсе.
        // reportGeoFailure вернёт паузу, если потерю сигнала ещё стоит
        // переждать, и 0 — если пора встать окончательно
        const miss = () => {
          const retryIn = reportGeoFailure(undefined);
          if (retryIn > 0) schedule(retryIn);
        };

        // Следующий опрос ставим только после ответа. Раньше цепочка
        // продолжалась и после промаха — именно это и рождало повторные
        // системные окна Telegram
        const poll = () => {
          // Предыдущий запрос ещё не ответил: при медленном GPS ответы
          // копились и метку дёргало устаревшими координатами
          if (geoBlockedRef.current || geoPendingRef.current) return;
          geoRequestIdRef.current += 1;
          const requestId = geoRequestIdRef.current;
          geoPendingRef.current = true;

          const settle = () => {
            clearTimeout(geoResponseTimerRef.current);
            geoResponseTimerRef.current = null;
            geoPendingRef.current = false;
          };

          // Предел ожидания — только после подтверждённого доступа.
          // Первый запрос ждёт, пока человек читает системное окно:
          // оборвать его — значит записать отказ, которого не было
          if (geoGrantedRef.current) {
            geoResponseTimerRef.current = setTimeout(() => {
              if (geoRequestIdRef.current !== requestId) return;
              geoRequestIdRef.current += 1; // поздний ответ уже не примем
              settle();
              statsRef.current.timeouts += 1;
              miss();
            }, GEO_RESPONSE_TIMEOUT_MS);
          }

          try {
            lm.getLocation((loc) => {
              if (geoRequestIdRef.current !== requestId) return;
              settle();
              if (!loc) {
                miss();
                return;
              }
              acceptPosition(loc.latitude, loc.longitude, loc.horizontal_accuracy);
              schedule(GEO_POLL_MS);
            });
          } catch (error) {
            // SDK бросает, если менеджер не готов или гео нет на устройстве.
            // Без перехвата pending оставался true, и опрос вставал навсегда
            geoRequestIdRef.current += 1;
            settle();
            statsRef.current.lastError = error?.message || String(error);
            miss();
          }
        };

        poll(); // сразу первый запрос
      } else if (source === 'browser') {
        // Браузер: watchPosition сам обновляет без повторных запросов
        geoBlockedRef.current = false;
        geoFailSinceRef.current = 0;
        watchIdRef.current = navigator.geolocation.watchPosition(
          (pos) => acceptPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
          (err) => reportGeoFailure(err),
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 5000 }
        );
      } else {
        showGeoError(null);
      }
    };

    if (source === 'telegram' && !lm.isInited) {
      lm.init(() => doStart());
    } else {
      doStart();
    }
  }, [showGeoError, acceptPosition, reportGeoFailure]);

  /** Начать слежение заново: сбросить прошлую попытку и все её блоки */
  const restartWatching = useCallback(() => {
    stopWatching();
    geoBlockedRef.current = false;
    geoFailSinceRef.current = 0;
    geoLostRetriesRef.current = 0;
    setGeoBlocked(false);
    statsRef.current.restarts += 1;
    startWatchingLocation();
  }, [stopWatching, startWatchingLocation]);

  // Спрашиваем один раз: разрешил — сразу начинаем следить, отказался —
  // не пристаём при каждом возврате на экран. Передумать можно кнопкой
  // геолокации на карте
  useEffect(() => {
    let answer = null;
    try { answer = localStorage.getItem(GEO_CONSENT_KEY); } catch {}
    if (answer === 'granted') {
      startWatchingLocation();
    } else if (answer !== 'denied') {
      setShowGeoPromptModal(true);
    }
    return () => stopWatching();
    // startWatchingLocation и stopWatching стабильны, но в deps их нет
    // намеренно: эффект — «один раз при входе»
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Возврат в приложение. Пока Telegram свёрнут или экран погашен,
   * таймеры стоят, ответы теряются, а браузерный watch может молча
   * перестать присылать точки. Вернулись, а позиция старая, — начинаем
   * заново. Только если координаты уже приходили: перезапуск тогда
   * окна не поднимет. После отказа здесь ничего не делаем
   */
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!geoGrantedRef.current) return;
      if (!watchIdRef.current && !geoBlockedRef.current) return;
      if (watchIdRef.current && Date.now() - lastFixAtRef.current < GEO_STALE_MS) return;
      restartWatching();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [restartWatching]);

  // Человек подвинул карту сам — перестаём её у него отбирать
  const handleUserPan = useCallback(() => setFollowMap(false), []);

  /**
   * «Попробовать снова» из панели с инструкцией: человек починил настройки
   * и хочет проверить, не закрывая приложение. Сбрасываем прошлую попытку —
   * иначе startWatchingLocation выйдет на первой же строке по watchIdRef,
   * и кнопка будет молча ничего не делать
   */
  const handleGeoRetry = useCallback(() => {
    telegramService.hapticFeedback('light');
    setGeoRetrying(true);

    stopWatching();
    geoBlockedRef.current = false;
    geoFailSinceRef.current = 0;
    geoLostRetriesRef.current = 0;
    geoTransientPanelRef.current = false;
    setGeoBlocked(false);

    // Панель закрываем только когда позиция реально пришла: если снова
    // не вышло, showGeoError покажет её опять с актуальной причиной
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      setGeoRetrying(false);
      setShowGeoAccessModal(false);
      setFollowMap(true);
    };

    const lm = getLocationManager();
    const source = pickGeoSource(lm, getPlatform(), !!navigator.geolocation);
    sourceRef.current = source;
    if (source === 'telegram') {
      const fail = () => { setGeoRetrying(false); showGeoError(undefined); };
      const ask = () => {
        try {
          lm.getLocation((loc) => {
            if (loc) {
              acceptPosition(loc.latitude, loc.longitude, loc.horizontal_accuracy);
              startWatchingLocation();
              done();
            } else {
              fail();
            }
          });
        } catch {
          fail();
        }
      };
      if (!lm.isInited) lm.init(ask); else ask();
    } else if (source === 'browser') {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          acceptPosition(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy);
          startWatchingLocation();
          done();
        },
        (err) => { setGeoRetrying(false); showGeoError(err); },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } else {
      setGeoRetrying(false);
      showGeoError(null);
    }
  }, [startWatchingLocation, showGeoError, stopWatching, acceptPosition]);

  // Пять быстрых тапов по кнопке геолокации — плашка диагностики
  const countDebugTap = useCallback(() => {
    const now = Date.now();
    const taps = debugTapsRef.current.filter((t) => now - t < DEBUG_TAPS_WINDOW_MS);
    taps.push(now);
    debugTapsRef.current = taps;
    if (taps.length < DEBUG_TAPS) return;
    debugTapsRef.current = [];
    setGeoDebug((on) => {
      try { localStorage.setItem(GEO_DEBUG_KEY, on ? '0' : '1'); } catch {}
      return !on;
    });
  }, []);

  // Кнопка геолокации. Заодно возвращает слежение: это единственный
  // способ включить его обратно
  const handleLocationBtn = useCallback(() => {
    countDebugTap();
    // Кнопка только включает слежение — центрирует MapFollower.
    // Раньше она центрировала сама, следом срабатывал follower,
    // и карта прыгала дважды
    setFollowMap(true);
    // Тап по кнопке — то же согласие: кто отказался, но передумал,
    // больше не увидит панель при следующем входе
    try { localStorage.setItem(GEO_CONSENT_KEY, 'granted'); } catch {}

    // Позиция свежая и слежение живо — follower сам вернёт карту.
    // По одному флагу «идёт» верить нельзя: запрос мог повиснуть,
    // и кнопка тогда молчала, а метка стояла на месте
    if (userPosition && watchIdRef.current && !geoBlockedRef.current
        && Date.now() - lastFixAtRef.current < GEO_STALE_MS) return;

    // Слежение могло быть не запущено — при первом входе человек отказался,
    // а теперь передумал, — остановлено после долгой потери сигнала
    // или зависло без ответа
    restartWatching();
  }, [userPosition, restartWatching, countDebugTap]);

  const acceptPrompt = useCallback(() => {
    try { localStorage.setItem(GEO_CONSENT_KEY, 'granted'); } catch {}
    setShowGeoPromptModal(false);
    startWatchingLocation();
  }, [startWatchingLocation]);

  const declinePrompt = useCallback(() => {
    try { localStorage.setItem(GEO_CONSENT_KEY, 'denied'); } catch {}
    setShowGeoPromptModal(false);
  }, []);

  // Снимок для плашки диагностики — она опрашивает его сама раз в секунду
  const getGeoDebugInfo = useCallback(() => ({
    platform: getPlatform(),
    source: sourceRef.current,
    watching: !!watchIdRef.current,
    blocked: geoBlockedRef.current,
    granted: geoGrantedRef.current,
    pending: geoPendingRef.current,
    lastFixAt: lastFixAtRef.current,
    lostRetries: geoLostRetriesRef.current,
    ...statsRef.current,
  }), []);

  return {
    userPosition,
    userAccuracy,
    geoBlocked,
    followMap,
    setFollowMap,
    handleUserPan,
    handleLocationBtn,
    handleGeoRetry,
    geoDebug,
    getGeoDebugInfo,
    // Для окон GeoModals
    showGeoPromptModal,
    setShowGeoPromptModal,
    acceptPrompt,
    declinePrompt,
    showGeoAccessModal,
    setShowGeoAccessModal,
    geoErrorKind,
    geoRetrying,
  };
};

export default useWalkGeolocation;
